import { Stethoscope, Hammer, Compass, GraduationCap, Brush, ArrowUpRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const SERVICES = [
    {
        id: "diagnostic",
        title: "Diagnostic des pathologies",
        icon: Stethoscope,
        size: "tall",
        text: "Humidité, fissures, salpêtre, ventilation : une analyse fine pour comprendre les désordres avant d'agir.",
        bullets: ["Visite de site", "Rapport détaillé", "Conseil & confort dans le bâti ancien"],
    },
    {
        id: "reparations",
        title: "Réparations en matériaux biosourcés",
        icon: Hammer,
        size: "wide",
        text: "Enduits chaux, plâtre, argile, terre, chanvre : restauration sensible et décoration respectueuse du bâti ancien.",
        bullets: ["Enduits chaux & plâtre", "Argile, terre & chanvre", "Restauration sensible"],
    },
    {
        id: "peinture",
        title: "Peintures compatibles bâti ancien",
        icon: Brush,
        size: "regular",
        text: "Peintures respirantes à base de chaux, caséine, silicate ou terre — sans solvant, laissant respirer les murs anciens.",
        bullets: ["Badigeons de chaux", "Peintures minérales", "Décoration"],
    },
    {
        id: "accompagnement",
        title: "Accompagnement de travaux",
        icon: Compass,
        size: "regular",
        text: "Un partenaire indépendant à vos côtés : du cahier des charges au suivi de chantier, en passant par la hiérarchisation des travaux.",
        bullets: ["Cahier des charges", "Hiérarchisation des travaux", "Suivi de chantier"],
    },
    {
        id: "formations",
        title: "Formations personnalisées",
        icon: GraduationCap,
        size: "full",
        text: "Modules sur-mesure pour particuliers et professionnels, théorie & pratique sur chantier-école.",
        bullets: ["Initiation chaux", "Diagnostic du bâti ancien"],
    },
];

export default function Services() {
    const ref = useReveal();

    return (
        <section
            id="services"
            data-testid="services-section"
            ref={ref}
            className="relative py-24 md:py-32"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="reveal max-w-3xl mb-14 md:mb-20">
                    <span className="label-overline text-brand">Ce que je propose</span>
                    <h2 className="mt-4 font-heading text-3xl md:text-5xl font-light tracking-tighter text-ink leading-tight">
                        Cinq métiers, <span className="italic">un seul fil rouge :</span>
                        <br />
                        respecter la maison.
                    </h2>
                </div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 md:grid-cols-6 grid-rows-auto gap-4 md:gap-5">
                    {SERVICES.map((s, idx) => {
                        const Icon = s.icon;
                        const span =
                            s.size === "tall"
                                ? "md:col-span-2 md:row-span-2"
                                : s.size === "wide"
                                ? "md:col-span-4"
                                : s.size === "full"
                                ? "md:col-span-6"
                                : "md:col-span-2";
                        return (
                            <article
                                key={s.id}
                                data-testid={`service-card-${s.id}`}
                                className={`reveal hover-lift relative bg-card border border-border rounded-sm p-7 md:p-9 flex flex-col ${span}`}
                                style={{ transitionDelay: `${idx * 80}ms` }}
                            >
                                <div className="flex items-start justify-between mb-6">
                                    <div className="w-11 h-11 rounded-sm bg-sand/70 flex items-center justify-center text-brand">
                                        <Icon size={20} strokeWidth={1.6} />
                                    </div>
                                    <ArrowUpRight size={18} className="text-stone2 opacity-50" />
                                </div>
                                <h3 className="font-heading text-xl md:text-2xl font-medium tracking-tight text-ink leading-snug">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm md:text-base text-stone2 leading-relaxed">
                                    {s.text}
                                </p>
                                <ul className="mt-auto pt-6 flex flex-wrap gap-2">
                                    {s.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="text-xs px-3 py-1.5 bg-cream border border-border text-ink/80 rounded-sm"
                                        >
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
